import React, { useContext } from 'react';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ClearIcon from '@mui/icons-material/Clear';
import { iterationKey, repeatIfDaily } from '../components/calendar/utils';
import { PracticeType } from './calendarModels';
import { CalendarUhaContext } from './calendarUhaContext';
import {
  PraksiskalenderAktivitetDto,
  PraksisklasseDto,
} from '../api/generated/studiested-generated-api';
import CalendarCell from './calendarCell';
import { formatIsoTzLess, getWeekDay, WeekYear } from '../date.utils';
import { BrukerrollerDto } from '../api/generated/praksissted-generated-api';
import { UhaUserContext } from '../user/UhaUserStore';
import { listIsDefinedAndHasElements } from '../util/listutils';

interface RowProps {
  practiceType: PracticeType;
  shouldRenderHeader: boolean;
  klasser: PraksisklasseDto[];
  aktiviteter: PraksiskalenderAktivitetDto[];
  hasGroups: boolean;
  isFirstGroup: boolean;
  onAddGroup: () => void;
  onDeleteGroup: (klasser: PraksisklasseDto[]) => void;
}

const CalendarRow: React.FC<React.PropsWithChildren<RowProps>> = ({
  practiceType,
  shouldRenderHeader,
  klasser,
  aktiviteter,
  hasGroups,
  isFirstGroup,
  onAddGroup,
  onDeleteGroup,
}) => {
  const {
    weekNumbers,
    earliestVisibleIsoDate,
    isWeekly,
    onCellClick,
    handleWeekHover,
    startYear,
  } = useContext(CalendarUhaContext);
  const aktivitetPrDag: {
    [startDato: string]: PraksiskalenderAktivitetDto;
  } = {};
  (aktiviteter || []).forEach(
    (aktivitet) => (aktivitetPrDag[aktivitet.startDato] = aktivitet),
  );

  const lastRenderedDate =
    weekNumbers.length > 0
      ? formatIsoTzLess(getWeekDay(weekNumbers[weekNumbers.length - 1], 6))
      : '';

  const getExtendedPracticePeriod = (
    week: WeekYear,
    day: number,
    practiceType: PracticeType,
  ): PraksiskalenderAktivitetDto | null => {
    if (!listIsDefinedAndHasElements(aktiviteter)) {
      return null;
    }
    const periodForWeekday = (weekDay: number) => {
      const thisDayIso = formatIsoTzLess(getWeekDay(week, weekDay));
      const isFirstColumn = week === weekNumbers[0] && weekDay === 0;
      return (
        aktivitetPrDag[thisDayIso] ||
        (isFirstColumn &&
          aktiviteter.find(
            (it) =>
              it.startDato < earliestVisibleIsoDate &&
              it.sluttDato >= thisDayIso,
          ))
      );
    };
    const outputForPeriod = (
      period: PraksiskalenderAktivitetDto | undefined,
    ) =>
      period
        ? {
            ...period,
            emneName: practiceType.emneName,
            practiceTypeName: practiceType.name,
          }
        : null;

    if (!isWeekly) {
      return outputForPeriod(periodForWeekday(day - 1));
    } else {
      const period = [0, 1, 2, 3, 4, 5, 6]
        .map(periodForWeekday)
        .find((it) => it);
      return outputForPeriod(period);
    }
  };

  const dayToCellMappingFunc = (
    dayOfWeek: number,
    week: WeekYear,
    practiceType: PracticeType,
    klasser: PraksisklasseDto[],
  ) => {
    const period = (day: number) =>
      getExtendedPracticePeriod(week, day, practiceType);
    return (
      <CalendarCell
        key={iterationKey(week, dayOfWeek)}
        isWeekly={isWeekly}
        weekYear={week}
        dayOfWeek={dayOfWeek}
        klasser={klasser}
        periodsReloaded={() => {}} //TODO: brukes denne?
        onClick={onCellClick}
        mouseIn={handleWeekHover}
        mouseOut={handleWeekHover}
        aktivitet={period(dayOfWeek)}
        startYear={startYear}
        lastRenderedDate={lastRenderedDate}
      />
    );
  };

  const { uhaUser: bruker } = useContext(UhaUserContext);
  const klassenavn = klasser[0].presentertNavn || klasser[0].bokstav;

  return (
    <>
      {shouldRenderHeader && (
        <tr className="practice-type" data-cy="calendar-praksistype">
          <td>{practiceType.name}</td>
          {weekNumbers.map((it: WeekYear) => {
            return repeatIfDaily(isWeekly, (iDay) => (
              <td key={iterationKey(it, iDay)}>&nbsp;</td>
            ));
          })}
        </tr>
      )}
      <tr className={`emne ${hasGroups ? 'with-groups' : ''}`}>
        <td>
          <div className="caption">
            <span className="code" data-cy="calendar-emnekode">
              {practiceType.emneCode}
            </span>
            {hasGroups ? (
              <span
                title={klassenavn}
                className="group-letter"
                data-cy="calendar-klassenavn"
              >
                {' '}
                - {klassenavn}
              </span>
            ) : (
              ''
            )}
            <br />
            {practiceType.emneName}
          </div>
          {bruker &&
            bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet && (
              <div
                className="groups-controls"
                data-cy="calendar-groups-controls"
              >
                {isFirstGroup ? (
                  <div className="add-group" onClick={onAddGroup}>
                    <GroupAddIcon />
                  </div>
                ) : (
                  <div
                    className="delete-group"
                    data-cy="calendar-delete-group"
                    onClick={() => onDeleteGroup(klasser)}
                  >
                    <ClearIcon />
                  </div>
                )}
              </div>
            )}
        </td>
        {weekNumbers.map((week: WeekYear) =>
          repeatIfDaily(isWeekly, (iDay) =>
            dayToCellMappingFunc(iDay, week, practiceType, klasser),
          ),
        )}
      </tr>
    </>
  );
};

export default CalendarRow;
